import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { IApiServiceError, usePortfolioConstructionActions } from "store";
import {
  ActiveSliceType,
  AISource,
  IObjectiveSaveItem,
  IObjectivesResponse,
  IObjectivesSaveRequest,
  ObjectiveConstraintKey,
  SliceGroup,
  SliceInvestor
} from "store/types";

import { resolveSlice } from "./sliceResolution";
import { OBJECTIVE_CONSTRAINT_KEYS } from "./useSliceData";

// Validation errors come back keyed by request field (e.g. `objectives.target-return.value`
// or `aum`). Find the first error whose field path mentions the given key.
const errorForKey = (errors: Record<string, string>, key: string): string | undefined =>
  errors[key] ?? Object.entries(errors).find(([field]) => field.includes(key))?.[1];

interface UseObjectiveMutationsParams {
  activeSliceType: ActiveSliceType | null;
  activeSliceKey: string | null;
  selectedInvestorId: SliceInvestor["id"] | null;
  selectedGroupId: SliceGroup["id"] | null;
  // Push a fresh response into the objectives store after a successful save
  // (data itself is loaded by `useSliceData`).
  setObjectivesData: (data: IObjectivesResponse) => void;
  onObjectivesChanged?: () => void;
}

// Owns the objective edit flow for the active slice: per-field/AUM validation
// errors plus every upsert/reset mutation and the AI-source lookup. Errors are
// cleared whenever the active slice changes.
export const useObjectiveMutations = ({
  activeSliceType,
  activeSliceKey,
  selectedInvestorId,
  selectedGroupId,
  setObjectivesData,
  onObjectivesChanged
}: UseObjectiveMutationsParams) => {
  const { saveObjectivesAction, getObjectiveSourcesAction } = usePortfolioConstructionActions();

  const [objectiveErrors, setObjectiveErrors] = useState<Record<string, string>>({});

  // Bulk-upsert objectives for the active owner (investor or slice). Returns
  // field-keyed validation errors on failure, or null on success.
  const saveObjectives = useCallback(
    async (body: IObjectivesSaveRequest): Promise<Record<string, string> | null> => {
      const slice = resolveSlice(activeSliceType, selectedInvestorId, selectedGroupId);
      if (!slice) return null;
      try {
        const res = await saveObjectivesAction(slice.sliceId, slice.sliceType, body);
        setObjectivesData(res);
        return null;
      } catch (e) {
        const fieldErrors = (e as IApiServiceError)?.errors;
        if (fieldErrors && Object.keys(fieldErrors).length > 0) {
          const mapped: Record<string, string> = {};
          for (const [field, messages] of Object.entries(fieldErrors)) {
            mapped[field] = Array.isArray(messages) ? messages.join(" ") : String(messages);
          }
          return mapped;
        }
        toast.error((e as IApiServiceError)?.message || "Could not save. Try again.");
        return null;
      }
    },
    [activeSliceType, selectedInvestorId, selectedGroupId, saveObjectivesAction, setObjectivesData]
  );

  // Runs a save, then distributes any validation errors onto the per-constraint
  // error map and the standalone AUM error slot.
  const applyObjectiveSave = useCallback(
    async (body: IObjectivesSaveRequest) => {
      const errors = await saveObjectives(body);
      const nextObjErrors: Record<string, string> = {};
      if (errors) {
        OBJECTIVE_CONSTRAINT_KEYS.forEach((key) => {
          const msg = errorForKey(errors, key);
          if (msg) nextObjErrors[key] = msg;
        });
      }
      setObjectiveErrors(nextObjErrors);
      if (!errors) onObjectivesChanged?.();
    },
    [saveObjectives, onObjectivesChanged]
  );

  const clearObjectiveError = useCallback((key: string) => {
    setObjectiveErrors((prev) => {
      if (!(key in prev)) return prev;
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  }, []);

  const updateObjective = useCallback(
    (key: string, value: number) =>
      applyObjectiveSave({ objectives: { [key]: { value, source: "user-override" } as IObjectiveSaveItem } }),
    [applyObjectiveSave]
  );

  const applyAIObjective = useCallback(
    (key: string, sourceLabel: string, value: number, url: string) =>
      applyObjectiveSave({
        objectives: {
          [key]: { value, source: "ai", source_label: sourceLabel, source_url: url } as IObjectiveSaveItem
        }
      }),
    [applyObjectiveSave]
  );

  // `value: null` tells the server to reset the constraint to its system default.
  const resetObjective = useCallback(
    (key: string) => applyObjectiveSave({ objectives: { [key]: { value: null } as IObjectiveSaveItem } }),
    [applyObjectiveSave]
  );

  const resetAllObjectives = useCallback(() => {
    const objectivesBody = OBJECTIVE_CONSTRAINT_KEYS.reduce<Record<string, IObjectiveSaveItem>>((acc, key) => {
      acc[key] = { value: null };
      return acc;
    }, {});
    return applyObjectiveSave({ objectives: objectivesBody });
  }, [applyObjectiveSave]);

  const loadAISources = useCallback(
    async (key: string): Promise<AISource[]> => {
      const sources = await getObjectiveSourcesAction(key as ObjectiveConstraintKey);
      return sources.map((s) => ({
        provider: s.provider,
        publication: s.publication,
        label: [s.provider, s.publication].filter(Boolean).join(" "),
        value: s.recommended_value,
        url: s.source_url
      }));
    },
    [getObjectiveSourcesAction]
  );

  // Clear edit errors whenever the active slice changes.
  useEffect(() => {
    setObjectiveErrors({});
  }, [activeSliceKey]);

  return {
    objectiveErrors,
    clearObjectiveError,
    updateObjective,
    applyAIObjective,
    resetObjective,
    resetAllObjectives,
    loadAISources
  };
};
